import type { Range } from "./portfolioData";

export type PortfolioMiniChartResponse = {
  range: Range;
  points: Array<{ date: string; index: number }>;
  returnPct: number | null;
  unavailable: string[];
};

const portfolioMiniChartCache = new Map<string, { expiresAt: number; promise: Promise<PortfolioMiniChartResponse> }>();
const portfolioMiniChartCacheTtl = 5 * 60 * 1000;

function portfolioMiniChartCacheKey(holdings: Array<{ symbol: string; weight: number }>, range: Range) {
  const normalizedHoldings = [...holdings]
    .map(({ symbol, weight }) => ({ symbol: symbol.toUpperCase(), weight: Number(weight) }))
    .sort((left, right) => left.symbol.localeCompare(right.symbol));
  return `${range}:${JSON.stringify(normalizedHoldings)}`;
}

export async function loadPortfolioMiniChart(holdings: Array<{ symbol: string; weight: number }>, range: Range): Promise<PortfolioMiniChartResponse> {
  const cacheKey = portfolioMiniChartCacheKey(holdings, range);
  const cached = portfolioMiniChartCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;

  const params = new URLSearchParams({ holdings: JSON.stringify(holdings), range, v: "1" });
  const promise = fetch(`/api/stocks/portfolio-mini-chart?${params.toString()}`)
    .then(async (response) => {
      if (!response.ok) {
        const errorPayload = await response.json<{ error?: string }>().catch(() => null);
        throw new Error(errorPayload?.error ?? "Portfolio chart is unavailable.");
      }
      const payload = await response.json<PortfolioMiniChartResponse>().catch(() => null);
      if (!payload) throw new Error("Portfolio chart is unavailable.");
      return payload;
    });

  portfolioMiniChartCache.set(cacheKey, { expiresAt: Date.now() + portfolioMiniChartCacheTtl, promise });
  void promise.catch(() => {
    if (portfolioMiniChartCache.get(cacheKey)?.promise === promise) portfolioMiniChartCache.delete(cacheKey);
  });
  return promise;
}
